import {
  createGenerateClassName,
  Theme,
  ThemeOptions,
  createTheme
} from '@material-ui/core/styles'
import { Overrides } from '@material-ui/core/styles/overrides'
import castToPalette from 'utils/url/castToPalette'
import getUrlPalette from 'utils/url/getUrlPalette'
import { isConfigSmava } from 'utils/url'

const urlPalette = castToPalette(getUrlPalette())
const isSmavaConfig = isConfigSmava()

export const FC_THEME_OPTIONS: ThemeOptions = {
  typography: {
    fontFamily: [
      'Lato',
      '-apple-system',
      'BlinkMacSystemFont',
      'sans-serif'
    ].join(',')
  },
  ...(urlPalette && { palette: urlPalette })
}

export const SMAVA_THEME_OPTIONS: ThemeOptions = {
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      'sans-serif'
    ].join(',')
  },
  ...(urlPalette && { palette: urlPalette })
}

export const COMMON_THEME_OVERRIDES = {
  MuiAccordion: {
    root: {
      '& .MuiButton-Collapse-entered': {
        margin: 0
      }
    }
  },
  MuiCollapse: {
    entered: {
      margin: 0
    }
  }
}

const TEST_THEME_OVERRIDES = {
  h1: {
    fontSize: '1.75rem',
    fontWeight: 700
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: 700
  },
  h3: {
    fontSize: '1.25rem',
    fontWeight: 700
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 700
  },
  h5: {
    fontSize: '1rem',
    fontWeight: 700
  }
}

export const SMAVA_THEME_OVERRIDES: (theme: Theme) => Overrides = (theme) => ({
  ...COMMON_THEME_OVERRIDES,
  MuiLink: {
    root: {
      color: theme.palette.primary.main,
      fontWeight: 700
    }
  },
  MuiTypography: {
    h1: {
      margin: '0',
      fontSize: '3rem',
      lineHeight: '4rem',
      fontWeight: 700,
      color: theme.palette.text.primary,

      [theme.breakpoints.down('sm')]: {
        fontSize: '2.375rem',
        lineHeight: '2.75rem'
      },
      ...(!isSmavaConfig && { ...TEST_THEME_OVERRIDES.h1 })
    },
    h2: {
      margin: '0',
      fontSize: '2rem',
      lineHeight: '2.5rem',
      fontWeight: 500,
      color: theme.palette.text.primary,

      [theme.breakpoints.down('sm')]: {
        fontSize: '1.625rem',
        lineHeight: '2.125rem'
      },
      ...(!isSmavaConfig && { ...TEST_THEME_OVERRIDES.h2 })
    },
    h3: {
      margin: '0',
      fontSize: '1.5rem',
      lineHeight: '2rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      ...(!isSmavaConfig && { ...TEST_THEME_OVERRIDES.h3 })
    },
    h4: {
      fontSize: '1.25rem',
      lineHeight: '1.75rem',
      fontWeight: 700,
      color: theme.palette.text.primary,

      [theme.breakpoints.down('sm')]: {
        fontSize: '1.125rem',
        lineHeight: '1.5rem'
      },
      ...(!isSmavaConfig && { ...TEST_THEME_OVERRIDES.h4 })
    },
    h5: {
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 500,
      color: theme.palette.grey[900],
      ...(!isSmavaConfig && { ...TEST_THEME_OVERRIDES.h5 })
    },
    h6: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      fontWeight: 500,
      color: theme.palette.grey[900]
    },
    body1: {
      fontSize: '1rem',
      lineHeight: '1.5rem',
      fontWeight: 400,
      color: theme.palette.grey[900]
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      fontWeight: 400,
      color: theme.palette.grey[900]
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: '1.25rem',
      fontWeight: 400,
      color: theme.palette.grey[900]
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: '1.75rem',
      fontWeight: 400,
      color: theme.palette.grey[900]
    },
    subtitle2: {
      fontSize: '0.875rem',
      lineHeight: '1.375rem',
      fontWeight: 500,
      color: theme.palette.grey[900]
    },
    button: {
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 500,
      color: theme.palette.grey[900]
    },
    overline: {
      fontSize: '0.75rem',
      lineHeight: '2rem',
      fontWeight: 400,
      color: theme.palette.grey[900]
    }
  },
  MuiRadio: {
    colorSecondary: {
      '&.Mui-checked': {
        color: theme.palette.primary.main
      }
    }
  },
  MuiDialogActions: {
    root: {
      '& .MuiButton-textPrimary': {
        color: theme.palette.secondary.dark
      }
    }
  },
  MuiSelect: {
    select: {
      '&:focus': {
        backgroundColor: theme.palette.primary.contrastText
      }
    }
  },
  MuiInputBase: {
    root: {
      height: '48px',

      '&:focus': {
        backgroundColor: theme.palette.primary.contrastText
      }
    }
  },
  MuiOutlinedInput: {
    root: {
      '& input': {
        color: theme.palette.grey[900]
      },
      '&:hover $notchedOutline': {
        borderColor: theme.palette.primary.main
      },
      '&$focused $notchedOutline': {
        boxShadow: `0 0 2px ${theme.palette.primary.main}`
      },
      '&$focused$error $notchedOutline': {
        boxShadow: `0 0 2px ${theme.palette.error.main}`
      }
    },
    notchedOutline: {
      borderWidth: '1px !important'
    }
  },
  MuiFormLabel: {
    root: {
      '&$focused': {
        color: theme.palette.grey[900]
      },
      '&$error': {
        color: theme.palette.grey[900]
      }
    }
  }
})

export const FC_THEME_OVERRIDES: (theme: Theme) => Overrides = (theme) => ({
  ...COMMON_THEME_OVERRIDES,
  MuiCssBaseline: {
    '@global': {
      a: {
        color: theme.palette.primary.dark
      }
    }
  },
  MuiLink: {
    root: {
      color: theme.palette.primary.main
    }
  },
  MuiTypography: {
    h1: {
      margin: '0',
      textAlign: 'center',
      fontSize: '1.75rem',
      lineHeight: '2.25rem',
      fontWeight: 'bold',
      color: theme.palette.primary.main
    },
    h2: {
      margin: '0',
      textAlign: 'center',
      fontSize: '1.5rem',
      lineHeight: '2rem',
      fontWeight: 'bold',
      color: theme.palette.primary.main
    },
    h3: {
      margin: '0',
      textAlign: 'center',
      fontSize: '1.25rem',
      lineHeight: '1.75rem',
      fontWeight: 'bold',
      color: theme.palette.primary.main
    },
    h4: {
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      fontWeight: 'bold',
      color: theme.palette.grey[900]
    },
    h5: {
      fontSize: '1rem',
      lineHeight: '1.375',
      fontWeight: 'bold',
      color: theme.palette.grey[900]
    }
  },
  MuiRadio: {
    colorSecondary: {
      '&.Mui-checked': {
        color: theme.palette.primary.main
      }
    }
  },
  MuiDialogActions: {
    root: {
      '& .MuiButton-textPrimary': {
        color: theme.palette.secondary.dark
      }
    }
  }
})

/* REMOVE_ME this is needed to run tests and support skeleton
We should kill it soon */
export const THEME: Theme = createTheme({})

export const generateClassName = createGenerateClassName({
  seed: 'taurine'
})
